import React from "react";
import { CircularProgress, Fade, FormControl, List, ListItem, TextField } from "@mui/material";
import { AttachmentValidationWorkingState, FileOrLink, IAttachmentFormPayload, IAttachmentValidator, ValidateAttachmentSignature } from "models/attachments";
import { ValidationHintText } from "components/shared";

type AttachmentLinkFormProps = {
    payload: IAttachmentFormPayload;
    disabled?: boolean;
    updatePayload: React.Dispatch<Partial<IAttachmentFormPayload>>;
    validator: IAttachmentValidator;
};

const AttachmentLinkForm: React.FC<AttachmentLinkFormProps> = (props) => {
    const {
        payload,
        updatePayload,
        validator,
        disabled
    } = props;

    //-------------------- HANDLERS --------------------
    const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileNameValue = (event.currentTarget as HTMLInputElement).value;
        updatePayload({
            name: fileNameValue
        });
    };

    const handleFileURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileUrlValue = (event.currentTarget as HTMLInputElement).value;
        updatePayload({
            url: fileUrlValue
        });
    };

    const handleFileNameBlur = async () => {
        await validator.validate('name', { name: payload.name, url: payload.url });
    };

    const handleFileUrlBlur = async () => {
        if (await validator.validate('url', { name: payload.name, url: payload.url })) {            
            if (payload.fileOrLink === FileOrLink.SFTP) {
                const newFileName = payload.url?.split('/').pop();
                updatePayload({
                    name: newFileName
                });
            }
        }
    };

    const isTypeSelected = (payload.typeId ?? 0) > 0;

    const isNameInvalid = !!validator.summary.name && validator.summary.name.length > 0;
    const isNameWorking = validator.state.mode === 'name' && validator.state.working;
    const isUrlWorking = validator.state.mode === 'url' && validator.state.working;

    const isUrlInvalid = !isUrlWorking && !!validator.summary.url && validator.summary.url.length > 0;
    const nameDisabled = !isTypeSelected || payload.fileOrLink === FileOrLink.SFTP;
    const urlDisabled = !isTypeSelected || (payload.fileOrLink === FileOrLink.SFTP && disabled)

    return (
        <List>
            <ListItem>
                <FormControl fullWidth>
                    <TextField
                        id="fileName"
                        name="fileName"
                        label="Attachment Name:"
                        fullWidth
                        value={payload.name}
                        onChange={handleFileNameChange}
                        error={isNameInvalid}
                        helperText={
                            <ValidationHintText name='name' 
                                hidden={isNameWorking}
                                validationSummary={validator.summary} 
                            />
                        }
                        required={true}
                        onBlur={handleFileNameBlur}
                        inputProps={{ maxLength: 256 }}
                        disabled={nameDisabled}
                        InputProps={{
                            endAdornment: isNameWorking ? <CircularProgress 
                                size={20}
                                sx={{                                
                                    position: 'unset'
                                }}
                            /> : null
                        }}
                    />
                </FormControl>
            </ListItem>            
            <ListItem>
                <TextField
                    id="fileURL"
                    name="fileURL"
                    label="File URL:"
                    fullWidth
                    value={payload.url}
                    onChange={handleFileURLChange}
                    error={isUrlInvalid}
                    helperText={
                        <ValidationHintText name='url' 
                            hidden={isNameWorking}
                            validationSummary={validator.summary} 
                        />
                    }
                    required={true}
                    onBlur={handleFileUrlBlur}
                    disabled={!isTypeSelected}
                    inputProps={{ 
                        maxLength: 350
                    }}
                    InputProps={{
                        endAdornment: isUrlWorking ? <CircularProgress 
                                size={20}
                                sx={{                                
                                    position: 'unset'
                                }}
                            /> : null                        
                    }}
                />
                
            </ListItem>            
        </List>
    );
};

export default AttachmentLinkForm;